import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';

// 1. Firebase 설정
import { fireAuth as auth, fireDB as db, appId } from './services/firebase';

// 2. 공통 컴포넌트
import Header from './components/common/Header';
import FeedbackModal from './components/common/FeedbackModal';

// 3. 화면(View) 컴포넌트
import LoginView from './components/views/LoginView';
import SignupView from './components/views/SignupView';
import HomeView from './components/views/HomeView';
import StageSelectView from './components/views/StageSelectView';
import Dashboard from './components/views/Dashboard';
import AdminView from './components/views/AdminView';
import { GoodbyeView } from './components/views/MiscViews';

// 4. 게임 & 오답통
import Game from './components/game/Game';
import Odaptong from './components/Odaptong';

export default function App() {
  // --- 상태 관리 ---
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [view, setView] = useState('login'); 
  const [loading, setLoading] = useState(true);
  
  // 게임 관련 상태
  const [selectedLaw, setSelectedLaw] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [gameModeType, setGameModeType] = useState('normal');
  const [homeCategory, setHomeCategory] = useState(null);
  
  // 모달 및 관리자 모드 상태
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false); // 관리자 모드 여부

  // --- 인증 및 데이터 로딩 ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        await fetchUserData(u.uid);
      } else {
        setUserData(null);
        setView('login');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchUserData = async (uid) => {
    try {
      const userRef = doc(db, 'artifacts', appId, 'users', uid, 'profile', 'info');
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // [수정] 로그인 기록 업데이트
        const data = userSnap.data();
        setUserData(data);
        await updateDoc(userRef, { 
          loginCount: increment(1),
          lastLogin: serverTimestamp() 
        });
        setView('home');
      } else {
        // Auth 계정은 있으나 DB 정보가 없는 경우 (likesaltt님 사례 해결)
        setView('signup');
      }
    } catch (error) { 
      console.error("사용자 정보 로딩 실패:", error); 
    }
  };

  // --- 핸들러 함수들 ---
  const handleLogout = () => {
    setView('goodbye');
    setTimeout(async () => {
      await signOut(auth);
      setUser(null);
      setUserData(null);
      setIsAdminMode(false); // 로그아웃 시 관리자 모드 해제
      setView('login');
    }, 2000);
  };

  const handleGoHome = () => {
    setView('home');
    setHomeCategory(null);
  };

  // 관리자 모드 전환 함수
  const handleAdminTransition = () => {
    const password = prompt("관리자 비밀번호를 입력하세요.");
    if (password === '19790703') {
      setIsAdminMode(true);
      setView('admin');
      alert("관리자 모드로 전환되었습니다.");
    } else {
      alert("비밀번호가 틀렸습니다.");
    }
  };

  if (loading) return <div className="flex h-screen items-center justify-center bg-slate-900 text-slate-300">로딩중...</div>;

  return (
    <div className="min-h-screen bg-slate-900 font-sans text-slate-100 selection:bg-indigo-500 selection:text-white overflow-x-hidden">
      
      {/* 헤더에 관리자 전환 함수 전달 */}
      <Header 
        user={user} 
        userData={userData} 
        setView={setView} 
        onLogout={handleLogout} 
        onHome={handleGoHome} 
        onAdminClick={handleAdminTransition}
        isAdminMode={isAdminMode}
      />

      <main className="max-w-4xl mx-auto p-4 md:p-6 pb-20">
        {view === 'login' && <LoginView setView={setView} />}
        
        {view === 'signup' && (
          <SignupView 
            onSuccess={(data) => { 
              setUserData(data); 
              setView('home'); // [수정] 대기 없이 즉시 홈 화면으로 이동
            }} 
            onBack={() => setView('login')} 
          />
        )}
        
        {view === 'goodbye' && <GoodbyeView />}
        {view === 'welcome' && <div className="text-center py-20 text-2xl text-indigo-400 animate-pulse">환영합니다!</div>}
        
        {view === 'home' && (
          <HomeView 
            setView={setView} 
            setSelectedLaw={setSelectedLaw} 
            setGameModeType={setGameModeType} 
            setSelectedStage={setSelectedStage}
            isAdmin={userData?.role === 'admin' || user?.email === 'doseungha@gmail.com'} 
            setShowFeedbackModal={setShowFeedbackModal} 
            category={homeCategory} 
            setCategory={setHomeCategory} 
          />
        )}
        
        {view === 'stage_select' && (
          <StageSelectView 
            setView={setView} 
            setSelectedStage={setSelectedStage} 
            selectedLaw={selectedLaw} 
            setHomeCategory={setHomeCategory} 
          />
        )}
        
        {view === 'dashboard' && <Dashboard user={user} userData={userData} setView={setView} />}
        
        {/* 관리자 뷰 */}
        {view === 'admin' && <AdminView user={user} setView={setView} />}
        
        {view === 'odaptong' && (
          <Odaptong 
            user={user} 
            setView={setView} 
            setGameModeType={setGameModeType} 
            onHome={handleGoHome} 
          />
        )}

        {view === 'game' && (
          <Game 
            key={selectedStage ? selectedStage.sessionKey : 'game-reset'}
            user={user} 
            userData={userData} 
            setView={setView} 
            selectedLaw={selectedLaw} 
            selectedStage={selectedStage} 
            setSelectedStage={setSelectedStage} 
            gameModeType={gameModeType} 
            onHome={handleGoHome} 
          />
        )}
      </main>

      {showFeedbackModal && <FeedbackModal user={user} onClose={() => setShowFeedbackModal(false)} />}
    </div>
  );
}